import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	MenuItem,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import PlanCentralService from '../../services/PlanCentralService';
import ClientService from '../../services/ClientService';
import AuthProvider from '../../providers/AuthProvider';
import GlobalTip from '../../providers/GlobalTip';
import { hasPermissionBypassAccess } from '../../components/layout/PermissionsMiddleware';
import { buildV3AdminPlanCentralDocsPath, buildV3ClientLivePlanEditWeeklyPath } from '../../services/ClientDashboardV3';

/**
 * Plan Central — admin view + controls of the V3 plan builder fleet
 * (planbuilderv3). Stage 1: the read-only fleet table. Stage 2: per-class
 * method flips (plan_policies MERGE; flips to AI Forecast also request a
 * fresh forecast) and rebuilds (forecast re-request, or a scoped run_seed
 * DAG trigger for lock classes) with manifest polling for feedback.
 */

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const epochMonthLabel = (epochMonth) => {
	const m = Number(epochMonth);
	if (!Number.isFinite(m) || m <= 0) return '—';
	return `${MONTH_LABELS[m % 12]} ${1970 + Math.floor(m / 12)}`;
};

const formatTs = (value) => {
	if (!value) return '—';
	const d = new Date(value);
	return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
};

// Epoch seconds — the fleet row and the class detail serialize the same
// timestamp with different precision, so string comparison would lie.
const toEpochSeconds = (value) => {
	const parsed = new Date(value || '');
	return Number.isNaN(parsed.getTime()) ? null : Math.floor(parsed.getTime() / 1000);
};

const STATUS_PRESENTATION = {
	FORECAST_BUILD: { label: 'Build queued', color: 'info' },
	BUILT_AFTER_VERDICT: { label: 'Built', color: 'success' },
	FORECAST_WAITING: { label: 'Waiting', color: 'warning' },
	FORECAST_BLOCKED_COVERAGE: { label: 'Blocked — coverage', color: 'error' },
	FORECAST_BLOCKED_NULL_VALUES: { label: 'Blocked — null values', color: 'error' },
	NEEDS_MIGRATION_SEED: { label: 'Needs seed', color: 'warning' },
	LOCK_TURN_BUILD: { label: 'Turn due', color: 'info' },
	LOCK_TURN_BLOCKED_STALE_SOURCE: { label: 'Stale source', color: 'error' },
	LOCK_WEEKLY_REFRESH: { label: 'Weekly due', color: 'info' },
	ALREADY_BUILT: { label: 'Built', color: 'success' },
	PAUSED: { label: 'Paused', color: 'default' },
	UNSUPPORTED_POLICY: { label: 'Unsupported policy', color: 'error' },
};

const StatusChip = ({ action }) => {
	const p = STATUS_PRESENTATION[action] || { label: action || '—', color: 'default' };
	return <Chip size="small" variant="outlined" color={p.color} label={p.label} />;
};

/**
 * The chip renders the manifest's LATEST verdict, which goes stale once the
 * verdict's own run builds the plan: fleet-wide re-evaluation only happens on
 * Sundays / the 1st, so a successful FORECAST_BUILD would keep reading
 * "Build queued" for days. A plan_settings header newer than the verdict
 * means the build already landed.
 */
const displayAction = (row) => {
	if (row.action === 'FORECAST_BUILD') {
		const built = toEpochSeconds(row.latest_header_created_at);
		const evaluated = toEpochSeconds(row.evaluated_at);
		if (built !== null && evaluated !== null && built > evaluated) return 'BUILT_AFTER_VERDICT';
	}
	return row.action;
};

const MethodChip = ({ policy, source }) => (
	<Tooltip title={`Resolved from: ${source || 'default'}`}>
		<Chip
			size="small"
			label={policy === 'ai_forecast' ? 'AI Forecast' : policy === 'lock' ? 'Lock' : policy || '—'}
			color={policy === 'ai_forecast' ? 'primary' : 'default'}
			variant={source === 'policy' ? 'filled' : 'outlined'}
		/>
	</Tooltip>
);

/**
 * Aggregate the per-class plan method for the header band. The method lives on
 * each class (plan_policies resolution), not on the client — uniform clients are
 * the norm today but mixed states are legitimate (mid-flip, partial onboarding).
 */
const summarizeMethods = (fleet) => {
	if (!fleet.length) return null;
	let ai = 0;
	let lock = 0;
	let other = 0;
	fleet.forEach((row) => {
		if (row.effective_policy === 'ai_forecast') ai += 1;
		else if (row.effective_policy === 'lock') lock += 1;
		else other += 1;
	});
	if (ai === fleet.length) return { label: 'AI Forecast', color: 'primary', detail: `${ai}/${fleet.length} classes` };
	if (lock === fleet.length) return { label: 'Lock', color: 'default', detail: `${lock}/${fleet.length} classes` };
	const parts = [];
	if (ai) parts.push(`${ai} AI Forecast`);
	if (lock) parts.push(`${lock} Lock`);
	if (other) parts.push(`${other} unresolved`);
	return { label: 'Mixed', color: 'warning', detail: parts.join(' / ') };
};

const ClientHeader = ({ client, fleet, fleetLoading, aiForecastCount, rebuildAllNote, onRebuildAll }) => {
	const methods = useMemo(() => summarizeMethods(fleet), [fleet]);
	if (!client) return null;
	return (
		<Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
			<Box sx={{ minWidth: 220 }}>
				<Typography variant="h6" sx={{ lineHeight: 1.2 }}>
					{client.code}{client.name ? ` — ${client.name}` : ''}
				</Typography>
				<Typography variant="caption" color="text.secondary">
					{fleetLoading ? 'Loading fleet…' : `${fleet.length} ${fleet.length === 1 ? 'class' : 'classes'} in the planning universe`}
				</Typography>
			</Box>
			{!fleetLoading && methods ? (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Chip size="small" color={methods.color} label={methods.label} />
					<Typography variant="body2" color="text.secondary">{methods.detail}</Typography>
				</Box>
			) : null}
			{!fleetLoading && fleet.length ? (
				<Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
					{rebuildAllNote ? (
						<>
							<CircularProgress size={14} />
							<Typography variant="caption" color="text.secondary">{rebuildAllNote}</Typography>
						</>
					) : null}
					<Tooltip title={aiForecastCount
						? 'One batch request; every forecast uploads under a single request id and ONE build DAG run creates all the plans together.'
						: 'No AI Forecast classes on this client.'}>
						<span>
							<Button
								size="small"
								variant="outlined"
								disabled={Boolean(rebuildAllNote) || !aiForecastCount}
								onClick={onRebuildAll}
							>
								Rebuild all AI Forecast{aiForecastCount ? ` (${aiForecastCount})` : ''}
							</Button>
						</span>
					</Tooltip>
				</Box>
			) : null}
		</Paper>
	);
};

const HealthTile = ({ label, value, note, tone }) => (
	<Paper sx={{ p: 1.5, flex: 1, minWidth: 200, borderTop: 3, borderTopColor: tone ? `${tone}.main` : 'divider' }}>
		<Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>{label}</Typography>
		<Typography variant="h6" sx={{ color: tone ? `${tone}.main` : 'text.primary' }}>{value}</Typography>
		{note ? <Typography variant="caption" color="text.secondary">{note}</Typography> : null}
	</Paper>
);

const METHOD_LABELS = { ai_forecast: 'AI Forecast', lock: 'Lock' };

const rowLabel = (row) => row.location_class_name || `Class ${row.location_class_id}`;

/**
 * A flip already declared but not yet adopted by a build: the manifest keeps
 * policy_source='policy' and suffixes action_reason with
 * "(declared policy X overrides header Y)".
 */
const isFlipPending = (row) => row.policy_source === 'policy' && /overrides header/i.test(row.action_reason || '');

const FLIP_BACK_WARNING = 'Flipping back to AI Forecast later needs the client on the AI forecast list AND a fresh forecast.';

const DetailPanel = ({ detail, loading }) => {
	if (loading) return <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={22} /></Box>;
	if (!detail) return null;
	const manifest = detail.manifest || {};
	const policy = detail.policy;
	return (
		<Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', p: 2, bgcolor: 'background.default' }}>
			<Box sx={{ minWidth: 280, flex: 1.2 }}>
				<Typography variant="overline" color="text.secondary">Why</Typography>
				<Typography variant="body2" sx={{ mb: 1 }}>{manifest.action_reason || '—'}</Typography>
				<Typography variant="caption" color="text.secondary" component="div">
					Verdict: {manifest.action || '—'} · anchor {epochMonthLabel(manifest.anchor_epoch_month)} · evaluated {formatTs(manifest.evaluated_at)}
				</Typography>
				<Typography variant="caption" color="text.secondary" component="div">
					Method resolution: {manifest.policy_source || 'default'} → {manifest.effective_policy || '—'}
					{policy ? ` (declared ${policy.policy} by ${policy.declared_by || '—'} at ${formatTs(policy.declared_at)})` : ' (no declaration)'}
				</Typography>
			</Box>
			<Box sx={{ minWidth: 250, flex: 1 }}>
				<Typography variant="overline" color="text.secondary">Version history</Typography>
				{(detail.headers || []).slice(0, 6).map((h) => (
					<Typography key={h.plan_settings_id} variant="body2" sx={{ py: 0.25, borderBottom: '1px dashed', borderColor: 'divider' }}>
						{epochMonthLabel(h.epoch_month)} · v{h.plan_version} · {h.plan_source || '—'}
					</Typography>
				))}
				{(detail.headers || []).length === 0 ? <Typography variant="body2" color="text.secondary">No V3 plans yet.</Typography> : null}
			</Box>
			<Box sx={{ minWidth: 250, flex: 1 }}>
				<Typography variant="overline" color="text.secondary">Forecast feed</Typography>
				{(detail.uploads || []).slice(0, 6).map((u) => (
					<Typography key={u.id} variant="body2" sx={{ py: 0.25, borderBottom: '1px dashed', borderColor: 'divider' }}>
						{formatTs(u.created_at)} · {u.status}
					</Typography>
				))}
				{(detail.uploads || []).length === 0 ? <Typography variant="body2" color="text.secondary">No forecasts ever requested.</Typography> : null}
			</Box>
		</Box>
	);
};

const PlanCentral = () => {
	const auth = AuthProvider.useAuth();
	const navigate = useNavigate();
	const [clients, setClients] = useState([]);
	const [client, setClient] = useState(null);
	const [health, setHealth] = useState(null);
	const [fleet, setFleet] = useState([]);
	const [fleetLoading, setFleetLoading] = useState(false);
	const [methodFilter, setMethodFilter] = useState('all');
	const [statusFilter, setStatusFilter] = useState('all');
	const [search, setSearch] = useState('');
	const [expandedLci, setExpandedLci] = useState(null);
	const [detail, setDetail] = useState(null);
	const [detailLoading, setDetailLoading] = useState(false);
	const [error, setError] = useState('');
	const [confirmAction, setConfirmAction] = useState(null);
	const [confirmBusy, setConfirmBusy] = useState(false);
	const [pendingByLci, setPendingByLci] = useState({});
	const [clientRebuildNote, setClientRebuildNote] = useState('');
	const pollTimersRef = useRef({});
	const clientRebuildTimerRef = useRef(null);
	const tip = GlobalTip.useMessage();

	useEffect(() => {
		(async () => {
			const response = await PlanCentralService.getHealth();
			if (response) setHealth(response);
			const clientResponse = await ClientService.listClientsSimple();
			const list = clientResponse?.data ?? clientResponse ?? [];
			setClients(Array.isArray(list) ? list : []);
		})();
	}, []);

	const stopWatch = useCallback((locationClassId) => {
		const timer = pollTimersRef.current[locationClassId];
		if (timer) clearTimeout(timer);
		delete pollTimersRef.current[locationClassId];
		setPendingByLci((prev) => {
			const next = { ...prev };
			delete next[locationClassId];
			return next;
		});
	}, []);

	const stopClientWatch = useCallback(() => {
		if (clientRebuildTimerRef.current) clearTimeout(clientRebuildTimerRef.current);
		clientRebuildTimerRef.current = null;
		setClientRebuildNote('');
	}, []);

	const stopAllWatches = useCallback(() => {
		Object.values(pollTimersRef.current).forEach(clearTimeout);
		pollTimersRef.current = {};
		setPendingByLci({});
		stopClientWatch();
	}, [stopClientWatch]);

	useEffect(() => stopAllWatches, [stopAllWatches]);

	useEffect(() => {
		stopAllWatches();
		if (!client) { setFleet([]); return; }
		(async () => {
			setFleetLoading(true);
			setError('');
			setExpandedLci(null);
			try {
				const response = await PlanCentralService.getFleet(client.id);
				setFleet(response?.data ?? []);
			} catch (e) {
				setError('Unable to load the plan fleet for this client.');
			} finally {
				setFleetLoading(false);
			}
		})();
	}, [client, stopAllWatches]);

	// Silent row refresh after an action lands (keeps the table in place).
	const reloadFleet = useCallback(async () => {
		if (!client) return;
		try {
			const response = await PlanCentralService.getFleet(client.id);
			setFleet(response?.data ?? []);
		} catch (e) {
			// keep the rows we have; the next manual reload will recover
		}
	}, [client]);

	// Manual refresh of snapshot + fleet, in place — no page reload needed.
	const refreshAll = useCallback(async () => {
		const response = await PlanCentralService.getHealth();
		if (response) setHealth(response);
		await reloadFleet();
	}, [reloadFleet]);

	const toggleDetail = async (locationClassId) => {
		if (expandedLci === locationClassId) { setExpandedLci(null); return; }
		setExpandedLci(locationClassId);
		setDetail(null);
		setDetailLoading(true);
		const response = await PlanCentralService.getClassDetail(locationClassId);
		setDetail(response || null);
		setDetailLoading(false);
	};

	/**
	 * Watch a rebuild DAG run: first the manifest verdict this run wrote for
	 * the class, then the run's terminal state. No manifest row once the run
	 * completed = it aborted before the manifest step.
	 */
	const beginRunWatch = (row, dagRunId) => {
		const lci = row.location_class_id;
		setPendingByLci((prev) => ({ ...prev, [lci]: `Rebuild run in flight (${dagRunId})` }));
		let attempts = 0;
		let verdictShown = false;
		const tick = async () => {
			attempts += 1;
			try {
				const status = await PlanCentralService.getRunStatus(dagRunId, lci);
				const manifest = status?.manifest;
				const state = status?.dag_run?.state;
				if (manifest && !verdictShown) {
					verdictShown = true;
					tip.showSuccess(`${rowLabel(row)} — run verdict: ${manifest.action} (${manifest.action_reason})`, 10);
				}
				if (state === 'success' || state === 'failed') {
					if (!manifest) {
						tip.show(`${rowLabel(row)} — the rebuild run ${state === 'failed' ? 'failed' : 'finished'} without a manifest row for this class (aborted before the manifest step).`, 10);
					} else if (state === 'failed') {
						tip.show(`${rowLabel(row)} — the rebuild run failed after the manifest step; check the DAG run.`, 10);
					} else {
						tip.showSuccess(`${rowLabel(row)} — rebuild run completed.`);
					}
					stopWatch(lci);
					reloadFleet();
					return;
				}
			} catch (e) {
				// transient poll error — keep watching
			}
			if (attempts >= 40) {
				tip.show(`${rowLabel(row)} — stopped watching the rebuild run after 10 minutes; refresh later to check.`);
				stopWatch(lci);
				return;
			}
			pollTimersRef.current[lci] = setTimeout(tick, 15000);
		};
		pollTimersRef.current[lci] = setTimeout(tick, 5000);
	};

	/**
	 * Watch a forecast request (method flip or forecast rebuild) all the way to
	 * the finished plan, in two phases: (1) the forecast completes in minutes
	 * and its upload triggers the build DAG, which writes a new manifest
	 * verdict; (2) a FORECAST_BUILD verdict's run is still writing the plan at
	 * that point, so keep watching until a plan header lands after the verdict
	 * — otherwise the row would sit on "Build queued" until a page refresh.
	 */
	const beginManifestWatch = (row) => {
		const lci = row.location_class_id;
		const baseline = toEpochSeconds(row.evaluated_at);
		setPendingByLci((prev) => ({ ...prev, [lci]: 'Fresh forecast requested — waiting for the next build verdict' }));
		let attempts = 0;
		let verdictAt = null;
		const tick = async () => {
			attempts += 1;
			try {
				const detailResponse = await PlanCentralService.getClassDetail(lci);
				const manifest = detailResponse?.manifest;
				const evaluatedAt = toEpochSeconds(manifest?.evaluated_at);
				if (verdictAt === null) {
					if (evaluatedAt !== null && (baseline === null || evaluatedAt > baseline)) {
						if (manifest.action === 'FORECAST_BUILD') {
							verdictAt = evaluatedAt;
							setPendingByLci((prev) => ({ ...prev, [lci]: 'Forecast passed the gates — the build run is writing the plan' }));
							tip.showSuccess(`${rowLabel(row)} — forecast passed the gates; the build run is writing the plan.`);
							reloadFleet();
						} else {
							tip.showSuccess(`${rowLabel(row)} — new verdict: ${manifest.action} (${manifest.action_reason})`, 10);
							stopWatch(lci);
							reloadFleet();
							return;
						}
					}
				}
				if (verdictAt !== null) {
					const headers = detailResponse?.headers || [];
					const built = headers.some((h) => {
						const createdAt = toEpochSeconds(h.created_at);
						return createdAt !== null && createdAt > verdictAt;
					});
					if (built) {
						tip.showSuccess(`${rowLabel(row)} — plan built.`);
						stopWatch(lci);
						reloadFleet();
						return;
					}
				}
			} catch (e) {
				// transient poll error — keep watching
			}
			if (attempts >= 30) {
				tip.show(`${rowLabel(row)} — still not finished after 15 minutes; the forecast or build may still be processing. Use the refresh button to check.`);
				stopWatch(lci);
				return;
			}
			pollTimersRef.current[lci] = setTimeout(tick, 30000);
		};
		pollTimersRef.current[lci] = setTimeout(tick, 30000);
	};

	/**
	 * Watch a client-wide rebuild: every forecast uploads under ONE
	 * trigger_request_id, and when the last one lands batch-forecast triggers
	 * ONE build DAG run (adhoc-<request id>) that creates all the plans in a
	 * single pass. Fleet rows adopt that run id as their manifest verdicts
	 * land; a plan header newer than the verdict means that class is built.
	 */
	const beginClientRunWatch = (expectedRunId) => {
		setClientRebuildNote('Forecasts uploading — one build run follows when the last one lands');
		let attempts = 0;
		let runSeen = false;
		const tick = async () => {
			attempts += 1;
			try {
				const response = await PlanCentralService.getFleet(client.id);
				const rows = response?.data ?? [];
				if (rows.length) setFleet(rows);
				const runRows = rows.filter((r) => r.last_run_id === expectedRunId);
				if (runRows.length) {
					if (!runSeen) {
						runSeen = true;
						setClientRebuildNote('Build run in progress — writing plans');
						tip.showSuccess(`One build run picked up ${runRows.length} ${runRows.length === 1 ? 'class' : 'classes'}; plans are being written.`);
					}
					const stillBuilding = runRows.filter((r) => displayAction(r) === 'FORECAST_BUILD');
					if (stillBuilding.length === 0) {
						const built = runRows.filter((r) => displayAction(r) === 'BUILT_AFTER_VERDICT').length;
						const rest = runRows.length - built;
						tip.showSuccess(`Client-wide rebuild completed in a single run — ${built} ${built === 1 ? 'plan' : 'plans'} built${rest ? `, ${rest} not buildable (see Why)` : ''}.`, 10);
						stopClientWatch();
						return;
					}
				}
			} catch (e) {
				// transient poll error — keep watching
			}
			if (attempts >= 40) {
				tip.show('Stopped watching the client-wide rebuild after 20 minutes — forecasts may still be processing. Refresh later to check.', 10);
				stopClientWatch();
				return;
			}
			clientRebuildTimerRef.current = setTimeout(tick, 30000);
		};
		clientRebuildTimerRef.current = setTimeout(tick, 30000);
	};

	const runConfirmedAction = async () => {
		if (!confirmAction) return;
		const { kind, row, targetMethod } = confirmAction;
		setConfirmBusy(true);
		try {
			if (kind === 'method') {
				const res = await PlanCentralService.setMethod(row.location_class_id, targetMethod);
				const fromTo = `${METHOD_LABELS[res?.previous_policy] || res?.previous_policy} → ${METHOD_LABELS[res?.policy] || res?.policy}`;
				if (res?.forecast_error) {
					tip.show(`${rowLabel(row)} — method declared (${fromTo}), but ${res.forecast_error}`, 12);
					reloadFleet();
				} else if (res?.forecast) {
					tip.showSuccess(`${rowLabel(row)} — method declared (${fromTo}); fresh forecast queued, the build runs when it completes.`, 10);
					beginManifestWatch(row);
				} else {
					tip.showSuccess(`${rowLabel(row)} — method declared (${fromTo}). The next Sunday/1st (or a forced run) adopts it.`, 10);
					reloadFleet();
				}
			} else if (kind === 'rebuild_all') {
				const res = await PlanCentralService.rebuildClientForecasts(client.id);
				const published = Number(res?.result?.tasks_published);
				tip.showSuccess(
					`Client-wide rebuild queued${Number.isFinite(published) && published > 0 ? ` — ${published} forecast tasks published` : ''}; one build run follows when the forecasts land.`,
					10
				);
				beginClientRunWatch(res?.expected_run_id || `adhoc-${res?.trigger_request_id}`);
			} else {
				const res = await PlanCentralService.rebuild(row.location_class_id);
				if (res?.mode === 'lock_seed') {
					tip.showSuccess(`${rowLabel(row)} — rebuild DAG triggered; watching the run.`);
					beginRunWatch(row, res.dag_run_id);
				} else {
					tip.showSuccess(`${rowLabel(row)} — fresh forecast requested; the build triggers itself when it completes.`);
					beginManifestWatch(row);
				}
			}
			setConfirmAction(null);
		} catch (e) {
			tip.show(e?.response?.data?.message || e?.message || 'The action failed.', 10);
		} finally {
			setConfirmBusy(false);
		}
	};

	const clientOptions = useMemo(() => clients.map((c) => ({
		id: c.Id ?? c.id,
		code: c.ClientCode ?? c.client_code ?? '',
		name: c.Name ?? c.ClientName ?? c.name ?? '',
	})).filter((c) => c.id), [clients]);

	const visibleFleet = useMemo(() => fleet.filter((row) => {
		if (methodFilter !== 'all' && row.effective_policy !== methodFilter) return false;
		if (statusFilter === 'attention' && !/BLOCKED|NEEDS|UNSUPPORTED/.test(row.action || '')) return false;
		if (search) {
			const needle = search.toLowerCase();
			const hay = `${row.location_class_id} ${row.location_class_name || ''} ${row.location_name || ''}`.toLowerCase();
			if (!hay.includes(needle)) return false;
		}
		return true;
	}), [fleet, methodFilter, statusFilter, search]);

	const aiForecastCount = useMemo(
		() => fleet.filter((row) => row.effective_policy === 'ai_forecast').length,
		[fleet]
	);

	const lastRun = health?.recent_runs?.[0];
	const owners = useMemo(() => Object.fromEntries((health?.owner_counts || []).map((o) => [o.owner, o.n])), [health]);

	// The planner routes sit behind full-planning; admins/support bypass like the route guard does.
	const canGoToPlan = hasPermissionBypassAccess(auth) || Boolean(auth?.checkPermission?.('full-planning'));

	const goToPlan = (row) => {
		const clientCode = row.client_code || client?.code;
		if (!clientCode) return;
		navigate(`${buildV3ClientLivePlanEditWeeklyPath(clientCode)}?lci=${row.location_class_id}`);
	};

	return (
		<Box sx={{ p: 2 }}>
			<Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 2, flexWrap: 'wrap' }}>
				<Typography variant="h5">Plan Central</Typography>
				<Typography variant="caption" color="text.secondary">
					V3 plan fleet · snapshot {formatTs(health?.generated_at)}
				</Typography>
				<Tooltip title="Refresh the snapshot and the fleet table in place">
					<span>
						<IconButton size="small" onClick={refreshAll} disabled={fleetLoading} aria-label="Refresh data">
							<RefreshIcon fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
				<Button
					size="small"
					variant="outlined"
					sx={{ ml: 'auto', alignSelf: 'center' }}
					onClick={() => navigate(window.location.pathname.startsWith('/v3')
						? buildV3AdminPlanCentralDocsPath()
						: '/admin/plan-central/docs')}
				>
					Documentation
				</Button>
			</Box>

			<Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
				<HealthTile
					label="Last pipeline run"
					value={lastRun ? `${lastRun.trigger_type}` : '—'}
					note={lastRun ? `${lastRun.manifest_rows} classes evaluated · ${formatTs(lastRun.created_at)}` : 'No runs logged'}
					tone="success"
				/>
				<HealthTile
					label="Fleet mix"
					value={`${owners.v3_lock ?? 0} lock · ${owners.v3_forecast ?? 0} forecast`}
					note={`${owners.seed_backlog ?? 0} seed backlog · ${owners.forecast_backlog ?? 0} forecast backlog`}
				/>
				<HealthTile
					label="Forecast uploads · 48h"
					value={`${health?.uploads_48h?.n ?? 0}`}
					note={`${health?.uploads_48h?.clients ?? 0} clients · ${health?.uploads_48h?.pending ?? 0} pending · ${health?.uploads_48h?.skipped ?? 0} skipped`}
					tone="info"
				/>
				<HealthTile
					label="Lost uploads · 48h"
					value={`${health?.lost_uploads?.n ?? 0}`}
					note={health?.lost_uploads?.n ? `${health.lost_uploads.clients} clients — write failures (see error log)` : 'No write failures'}
					tone={health?.lost_uploads?.n ? 'error' : 'success'}
				/>
			</Box>

			<Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
				<Autocomplete
					size="small"
					sx={{ minWidth: 280 }}
					options={clientOptions}
					value={client}
					onChange={(e, value) => setClient(value)}
					getOptionLabel={(o) => (o ? `${o.code} — ${o.name}` : '')}
					isOptionEqualToValue={(o, v) => o.id === v.id}
					renderInput={(params) => <TextField {...params} label="Client (required)" />}
				/>
				<TextField select size="small" label="Method" value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)} sx={{ minWidth: 140 }}>
					<MenuItem value="all">All</MenuItem>
					<MenuItem value="ai_forecast">AI Forecast</MenuItem>
					<MenuItem value="lock">Lock</MenuItem>
				</TextField>
				<TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 160 }}>
					<MenuItem value="all">All</MenuItem>
					<MenuItem value="attention">Needs attention</MenuItem>
				</TextField>
				<TextField size="small" label="Find class" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 180 }} />
				<Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
					{client ? `${visibleFleet.length} of ${fleet.length} classes` : 'Select a client to load its fleet'}
				</Typography>
			</Paper>

			<ClientHeader
				client={client}
				fleet={fleet}
				fleetLoading={fleetLoading}
				aiForecastCount={aiForecastCount}
				rebuildAllNote={clientRebuildNote}
				onRebuildAll={() => setConfirmAction({ kind: 'rebuild_all', row: null })}
			/>

			{error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

			<TableContainer component={Paper}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={{ width: 34 }} />
							<TableCell>Class</TableCell>
							<TableCell>Method</TableCell>
							<TableCell>Latest plan</TableCell>
							<TableCell>Status</TableCell>
							<TableCell>Why</TableCell>
							<TableCell align="right">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{fleetLoading ? (
							<TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
						) : visibleFleet.map((row) => {
							const shownAction = displayAction(row);
							const builtAfterVerdict = shownAction === 'BUILT_AFTER_VERDICT';
							const shownReason = builtAfterVerdict
								? `Plan created ${formatTs(row.latest_header_created_at)} by the queued build.`
								: (row.action_reason || '—');
							const actionsBlocked = Boolean(pendingByLci[row.location_class_id]) || Boolean(clientRebuildNote);
							return (
							<React.Fragment key={row.location_class_id}>
								<TableRow hover>
									<TableCell>
										<IconButton size="small" onClick={() => toggleDetail(row.location_class_id)} aria-label="Expand class detail">
											{expandedLci === row.location_class_id ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
										</IconButton>
									</TableCell>
									<TableCell>
										<Typography variant="body2" sx={{ fontWeight: 600 }}>{row.location_class_name || `Class ${row.location_class_id}`}</Typography>
										<Typography variant="caption" color="text.secondary">
											{row.location_class_id}{row.location_name ? ` · ${row.location_name}` : ''}
										</Typography>
									</TableCell>
									<TableCell><MethodChip policy={row.effective_policy} source={row.policy_source} /></TableCell>
									<TableCell>
										{row.latest_plan_epoch_month
											? <>
												<Typography variant="body2">{epochMonthLabel(row.latest_plan_epoch_month)} · v{row.latest_plan_version}</Typography>
												<Typography variant="caption" color="text.secondary">{row.latest_plan_source || ''}</Typography>
											</>
											: <Typography variant="body2" color="text.secondary">none</Typography>}
									</TableCell>
									<TableCell><StatusChip action={shownAction} /></TableCell>
									<TableCell sx={{ maxWidth: 320 }}>
										<Tooltip title={builtAfterVerdict ? `Verdict was: ${row.action_reason || '—'}` : (row.action_reason || '')}>
											<Typography variant="caption" color="text.secondary" noWrap component="div">{shownReason}</Typography>
										</Tooltip>
									</TableCell>
									<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
										{canGoToPlan ? (
											<Button size="small" onClick={() => goToPlan(row)}>Go to plan</Button>
										) : null}
										<Tooltip title={pendingByLci[row.location_class_id]
											|| (clientRebuildNote ? 'A client-wide rebuild is in flight — per-class actions resume when it lands' : '')
											|| (isFlipPending(row) ? 'A declared flip is already pending — the next build adopts it' : '')}>
											<span>
												<Button
													size="small"
													disabled={actionsBlocked || isFlipPending(row) || !METHOD_LABELS[row.effective_policy]}
													onClick={() => setConfirmAction({
														kind: 'method',
														row,
														targetMethod: row.effective_policy === 'ai_forecast' ? 'lock' : 'ai_forecast',
													})}
												>
													Change method
												</Button>
												<Button
													size="small"
													disabled={actionsBlocked || !METHOD_LABELS[row.effective_policy]}
													onClick={() => setConfirmAction({ kind: 'rebuild', row })}
												>
													Rebuild
												</Button>
											</span>
										</Tooltip>
										{pendingByLci[row.location_class_id] ? <CircularProgress size={14} sx={{ ml: 0.5, verticalAlign: 'middle' }} /> : null}
									</TableCell>
								</TableRow>
								{expandedLci === row.location_class_id ? (
									<TableRow>
										<TableCell colSpan={7} sx={{ p: 0, borderBottom: 2, borderColor: 'primary.light' }}>
											<DetailPanel detail={detail} loading={detailLoading} />
										</TableCell>
									</TableRow>
								) : null}
							</React.Fragment>
							);
						})}
						{!fleetLoading && client && visibleFleet.length === 0 ? (
							<TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}>
								<Typography variant="body2" color="text.secondary">No classes match the current filters.</Typography>
							</TableCell></TableRow>
						) : null}
					</TableBody>
				</Table>
			</TableContainer>

			<Dialog open={Boolean(confirmAction)} onClose={() => (confirmBusy ? null : setConfirmAction(null))} maxWidth="sm" fullWidth>
				{confirmAction ? (
					<>
						<DialogTitle>
							{confirmAction.kind === 'method'
								? `Change method to ${METHOD_LABELS[confirmAction.targetMethod]} — ${rowLabel(confirmAction.row)}`
								: confirmAction.kind === 'rebuild_all'
									? `Rebuild all AI Forecast classes — ${client?.code || ''}`
									: `Rebuild — ${rowLabel(confirmAction.row)}`}
						</DialogTitle>
						<DialogContent>
							{confirmAction.kind === 'rebuild_all' ? (
								<DialogContentText>
									Requests fresh forecasts for this client's <strong>{aiForecastCount} AI Forecast {aiForecastCount === 1 ? 'class' : 'classes'}</strong> in
									one batch request. Every forecast uploads under a single request id, and when the last one lands
									batch-forecast triggers <strong>one</strong> build DAG run that creates all the plans together — never a
									run per class. Lock classes ignore their uploads by design. Usually done within minutes.
								</DialogContentText>
							) : null}
							{confirmAction.kind === 'method' && confirmAction.targetMethod === 'lock' ? (
								<>
									<DialogContentText>
										Declares <strong>Lock</strong> for this class in plan_policies. Nothing rebuilds now —
										the next Sunday / 1st-of-month run (or a forced run) adopts the latest plan as its carry base.
									</DialogContentText>
									<Alert severity="warning" sx={{ mt: 2 }}>{FLIP_BACK_WARNING}</Alert>
								</>
							) : null}
							{confirmAction.kind === 'method' && confirmAction.targetMethod === 'ai_forecast' ? (
								<DialogContentText>
									Declares <strong>AI Forecast</strong> for this class and immediately requests a fresh forecast —
									forecasts uploaded while the class was lock-owned are terminally skipped, so a new one is required.
									The build DAG runs automatically once the forecast completes (usually minutes).
								</DialogContentText>
							) : null}
							{confirmAction.kind === 'rebuild' ? (
								<DialogContentText>
									{confirmAction.row.effective_policy === 'ai_forecast'
										? 'Requests a fresh forecast for this class; the build DAG triggers itself when the forecast completes (usually minutes).'
										: 'Triggers the build DAG with a scoped run_seed — this class is re-seeded from its latest V2 plan. The seed refuses to replace user-touched versions by design.'}
								</DialogContentText>
							) : null}
						</DialogContent>
						<DialogActions>
							<Button onClick={() => setConfirmAction(null)} disabled={confirmBusy}>Cancel</Button>
							<Button variant="contained" onClick={runConfirmedAction} disabled={confirmBusy}>
								{confirmBusy
									? <CircularProgress size={18} />
									: confirmAction.kind === 'method' ? 'Change method' : confirmAction.kind === 'rebuild_all' ? 'Rebuild all' : 'Rebuild'}
							</Button>
						</DialogActions>
					</>
				) : null}
			</Dialog>
		</Box>
	);
};

export default PlanCentral;
